import { useEffect } from 'react'
import { useRouteError, isRouteErrorResponse } from 'react-router-dom'
import { useReportError } from '@/contexts/ErrorReporterContext'
import { AlertCircle } from 'lucide-react'
import './error-boundary.css'

/**
 * Error boundary component for React Router routes.
 *
 * Catches errors thrown during route rendering and reports them
 * to the ErrorReporter context, opening the error report dialog.
 *
 * Usage: Add as `errorElement` on routes in routes.tsx
 */
export function RouteErrorBoundary() {
	const error = useRouteError()
	const reportError = useReportError()

	useEffect(() => {
		// Convert route error to Error object and report it
		if (error) {
			let errorObj: Error

			if (isRouteErrorResponse(error)) {
				// React Router error response (404, etc.)
				errorObj = new Error(`${error.status} ${error.statusText}: ${error.data}`)
				errorObj.name = 'RouteError'
			} else if (error instanceof Error) {
				errorObj = error
			} else {
				errorObj = new Error(String(error))
				errorObj.name = 'UnknownRouteError'
			}

			// Report to error dialog
			reportError(errorObj)
		}
	}, [error, reportError])

	// Render minimal fallback - the ErrorReportDialog handles the main UI
	return (
		<div className="error-boundary-fallback">
			<AlertCircle className="error-boundary-icon" />
			<h2 className="error-boundary-title">Something went wrong</h2>
			<p className="error-boundary-message">
				An error occurred while loading this page. An error report dialog should appear shortly.
			</p>
			<div className="error-boundary-actions">
				<button
					type="button"
					onClick={() => window.location.reload()}
					className="error-boundary-button"
					data-variant="primary"
				>
					Reload page
				</button>
			</div>
		</div>
	)
}
